import { api, tokens } from './client'

export type Me = {
  id: string
  username: string
  email: string
  firstname: string
  lastname: string
  roles: string[]
  permissions: string[]
  mfa_enrolled: boolean
  must_change_password: boolean
  last_login?: string | null
}

export type LoginChallenge = {
  step: 'enroll' | 'verify'
  user_id: string
  challenge: string
  otpauth_uri?: string
  secret?: string
}

export type TokenPair = {
  access_token: string
  refresh_token: string
  must_change_password?: boolean
}

/** Step 1: verify email + password. Returns the MFA step (enrol on first login, else verify). */
export async function staffLogin(login: string, password: string): Promise<LoginChallenge> {
  return api.postPublic<LoginChallenge>('/auth/staff/login', { login, password })
}

/** Step 2: verify the 6-digit TOTP code (enrols the secret on first login). Installs the tokens. */
export async function staffOtpVerify(challenge: string, code: string): Promise<TokenPair> {
  const data = await api.postPublic<TokenPair>('/auth/staff/otp/verify', { challenge, code })
  tokens.setAccess(data.access_token)
  tokens.setRefresh(data.refresh_token)
  return data
}

export type EmailOtpSent = { ok: boolean; sent_to: string; dev_code?: string }

/** Alternative MFA: email a 6-digit code to the account's address. */
export async function otpEmailSend(challenge: string): Promise<EmailOtpSent> {
  return api.postPublic<EmailOtpSent>('/auth/staff/otp/email/send', { challenge })
}

/** Verify an emailed 6-digit code. Installs the tokens on success. */
export async function otpEmailVerify(challenge: string, code: string): Promise<TokenPair> {
  const data = await api.postPublic<TokenPair>('/auth/staff/otp/email/verify', { challenge, code })
  tokens.setAccess(data.access_token)
  tokens.setRefresh(data.refresh_token)
  return data
}

export async function fetchMe(): Promise<Me> {
  return api.get<Me>('/auth/me')
}

/** Self-service / forced first-login password change. A forced change returns a fresh admin-scoped pair. */
export async function changePassword(current_password: string, new_password: string): Promise<void> {
  const data = await api.post<{ access_token?: string; refresh_token?: string }>('/auth/password', {
    current_password,
    new_password,
  })
  if (data?.access_token) tokens.setAccess(data.access_token)
  if (data?.refresh_token) tokens.setRefresh(data.refresh_token)
}

export async function serverLogout(): Promise<void> {
  try {
    await api.post('/auth/logout')
  } catch {
    /* local logout proceeds regardless */
  }
}
